/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, useRef } from 'react';
import { useLocation, useMatches } from 'react-router';
import { pushPageView, type RouteAnalytics } from './data-layer';

/** A loader whose data carries a `RouteAnalytics` object under `analytics`. */
type MatchWithAnalytics = { data?: { analytics?: RouteAnalytics } };

/**
 * Reads the leaf route's `analytics` value (returned by that page's loader) and pushes a `pageView` —
 * plus any page-type event — to `window.dataLayer` on every navigation (UCR-2938, Phase 1).
 *
 * The router owns page identity, so each page loader returns a stable `pageName`/`pageType` and its
 * context (product/listing/search/article); this component is the single choke point that emits them.
 * Mirrors `AdobeDataLayerTracker`: client-only, and deduped per path so a StrictMode remount or an
 * identical re-render doesn't double-push. Routes that don't return `analytics` simply push nothing.
 */
export function DataLayerTracker() {
    const location = useLocation();
    const matches = useMatches();
    const lastPushedRef = useRef<string | null>(null);

    // The leaf match is the most specific route; its loader carries the page's analytics payload.
    const analytics = (matches.at(-1) as MatchWithAnalytics | undefined)?.data?.analytics;

    useEffect(() => {
        if (typeof window === 'undefined' || !analytics) return;

        // Dedup on the resolved path (not the payload) so we push exactly once per navigation.
        const fullPath = `${location.pathname}${location.search}`;
        if (lastPushedRef.current === fullPath) return;
        lastPushedRef.current = fullPath;

        pushPageView(analytics);
    }, [analytics, location.pathname, location.search]);

    return null;
}
